import { useProjectActions, useProjectState } from '@hooks/useProject'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { PropsWithChildren, useEffect } from 'react'

export type BrowserCompatibilityStatus =
  | 'Compatible'
  | 'UnsupportedBrowser'
  | 'WebGlUnsupported'
  | 'IncorrectAngleBackend'

function checkBrowserCompatibility(): BrowserCompatibilityStatus {
  if (typeof navigator === 'undefined') return 'UnsupportedBrowser'

  const isMac =
    navigator.platform.toUpperCase().includes('MAC') ||
    navigator.userAgent.includes('Mac OS')

  if (!isMac) return 'Compatible'

  const isSafari = /^((?!chrome).)*safari/i.test(navigator.userAgent)
  if (isSafari) {
    // Mac running Safari: run everywhere
    return 'Compatible'
  }

  // Mac not running safari:

  const canvas = document.createElement('canvas')
  const gl = canvas.getContext('webgl2')
  if (!gl) {
    canvas.remove()
    return 'WebGlUnsupported'
  }

  // If it's an ANGLE backend, it should be metal based
  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
  if (!debugInfo) {
    canvas.remove()
    return 'UnsupportedBrowser'
  }

  const debugRenderer = gl
    .getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
    .toLowerCase()
  console.log('debugRenderer=' + debugRenderer)
  canvas.remove()

  const isAngle = debugRenderer.includes('angle')
  if (!isAngle) {
    return 'UnsupportedBrowser'
  }

  const isMetal = debugRenderer.includes('metal')
  if (!isMetal) {
    return 'IncorrectAngleBackend'
  }

  return 'Compatible'
}

function useChromeVersionCompatibilityCheck() {
  const {
    setHasSeenIncompatibleChromeVersionDialog,
    setShowIncompatibleChromeVersionDialog
  } = useProjectActions()

  const hasSeenIncompatibleChromeVersionDialog = useProjectState(
    'hasSeenIncompatibleChromeVersionDialog'
  )

  const flags = useFlags()
  const isIncompatibleChromeVersionDialogEnabled =
    flags['base-tf-ui-chrome-version-incompatibility-dialog']

  useEffect(() => {
    if (
      !isIncompatibleChromeVersionDialogEnabled ||
      hasSeenIncompatibleChromeVersionDialog
    ) {
      return
    }

    checkChromeVersion()
  }, [
    hasSeenIncompatibleChromeVersionDialog,
    isIncompatibleChromeVersionDialogEnabled
  ])

  async function checkChromeVersion() {
    if (!(navigator as any)?.userAgentData?.getHighEntropyValues) {
      return
    }

    const isMac =
      navigator.platform.toUpperCase().includes('MAC') ||
      navigator.userAgent.includes('Mac OS')
    if (!isMac) return

    const versions = await (
      navigator as any
    ).userAgentData.getHighEntropyValues(['fullVersionList'])
    if (!versions) return

    const chromeVersion = versions.fullVersionList.find(
      ({ brand }: { brand: string }) =>
        brand === 'Google Chrome' || brand === 'Chromium'
    )?.version

    if (chromeVersion === '136.0.7103.93') {
      setShowIncompatibleChromeVersionDialog(true)
      setHasSeenIncompatibleChromeVersionDialog(true)
    }
  }
}

export function BrowserCompatibilityChecker({ children }: PropsWithChildren) {
  const { setBrowserCompatibility } = useProjectActions()
  const browserCompatibility = useProjectState('browserCompatibility')

  useChromeVersionCompatibilityCheck()

  // Put in useEffect to ensure window is defined
  useEffect(() => {
    const compatibility = checkBrowserCompatibility()
    setBrowserCompatibility(compatibility)
  }, [])

  if (!browserCompatibility) return null
  return <>{children}</>
}
